import React, { useState, useMemo, useEffect } from 'react';
import {
  Row, Col, Card, DatePicker, Form, Spin, Alert, Table,
} from 'antd';
import moment from 'moment';
import useLogin from '../../hooks/useLogin';
import AnalyticsService from '../../services/analyticsService';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

const { RangePicker } = DatePicker;

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
);

function ChartsPage() {
  const { user } = useLogin();

  // Date range state - default to last month
  const [dateRange, setDateRange] = useState(() => {
    const endDate = moment.utc();
    const startDate = moment.utc().subtract(1, 'month');
    return [startDate, endDate];
  });

  const clientId = user?.client_id;

  // Analytics data state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle date range change - simple UTC date handling
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      // Set start to beginning of day and end to end of day in UTC
      const startOfDay = moment.utc(`${start.format('YYYY-MM-DD')} 00:00:00`);
      const endOfDay = moment.utc(`${end.format('YYYY-MM-DD')} 23:59:59`);

      setDateRange([startOfDay, endOfDay]);
    } else {
      setDateRange(null);
    }
  };

  // Get display values for date picker (show UTC dates as local dates for display)
  const getDisplayDateRange = () => {
    if (!dateRange) return null;
    return dateRange.map((date) => moment(date.format('YYYY-MM-DD')));
  };

  // Fetch analytics data when clientId or dateRange changes
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!clientId || !dateRange) return;

      setLoading(true);
      setError(null);

      try {
        const startDate = dateRange[0].format('YYYY-MM-DD');
        const endDate = dateRange[1].format('YYYY-MM-DD');

        const analyticsService = new AnalyticsService();
        const data = await analyticsService.getAnalyticsData(clientId, startDate, endDate);

        setAnalyticsData(data);
      } catch (err) {
        console.error('Failed to fetch analytics data:', err);
        setError(err.message || 'Failed to fetch analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [clientId, dateRange?.[0]?.valueOf(), dateRange?.[1]?.valueOf()]);

  // Process data for charts
  const chartData = useMemo(() => {
    if (!analyticsData) return null;

    const topFilms = AnalyticsService.processTopFilmsData(analyticsData);
    const topScreenings = AnalyticsService.processTopScreeningsData(analyticsData);
    const dailyActivity = AnalyticsService.processDailyActivityData(analyticsData);
    const totalActivity = AnalyticsService.processTotalActivityData(analyticsData);
    const projectOverview = AnalyticsService.processProjectOverviewData(analyticsData);
    const popularityTimeline = AnalyticsService.processPopularityTimelineData(analyticsData);

    return {
      topFilms,
      topScreenings,
      dailyActivity,
      totalActivity,
      projectOverview,
      popularityTimeline,
      rawData: analyticsData,
    };
  }, [analyticsData]);

  // Create chart data for TOP Films
  const topFilmsVoteCountData = useMemo(() => {
    if (!chartData?.topFilms?.totalVotes) return null;

    const films = chartData.topFilms.totalVotes;
    return {
      labels: films.map((film) => film.title),
      datasets: [
        {
          label: 'Total Vote Count',
          data: films.map((film) => film.totalVotes),
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [chartData]);

  const topFilmsScoreData = useMemo(() => {
    if (!chartData?.topFilms?.averageScore) return null;

    const films = chartData.topFilms.averageScore;
    return {
      labels: films.map((film) => film.title),
      datasets: [
        {
          label: 'Average Score (1-5)',
          data: films.map((film) => Math.round(film.averageScore * 100) / 100), // Round to 2 decimals
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [chartData]);

  // Create chart data for TOP Screenings
  const topScreeningsVoteCountData = useMemo(() => {
    if (!chartData?.topScreenings?.totalVotes) return null;

    const screenings = chartData.topScreenings.totalVotes;
    return {
      labels: screenings.map((screening) => screening.title),
      datasets: [
        {
          label: 'Total Vote Count',
          data: screenings.map((screening) => screening.totalVotes),
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [chartData]);

  const topScreeningsScoreData = useMemo(() => {
    if (!chartData?.topScreenings?.averageScore) return null;

    const screenings = chartData.topScreenings.averageScore;
    return {
      labels: screenings.map((screening) => screening.title),
      datasets: [
        {
          label: 'Average Score (1-5)',
          data: screenings.map((screening) => Math.round(screening.averageScore * 100) / 100), // Round to 2 decimals
          backgroundColor: 'rgba(255, 205, 86, 0.5)',
          borderColor: 'rgba(255, 205, 86, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [chartData]);

  // Create chart data for Daily User Activity (stacked bar/line)
  const dailyActivityData = useMemo(() => {
    if (!chartData?.dailyActivity) return null;

    const {
      labels, dailyVotes, dailyBannerClicks, dailyComments, uniqueUsers,
    } = chartData.dailyActivity;
    return {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Daily Vote Count',
          data: dailyVotes,
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
          yAxisID: 'y',
        },
        {
          type: 'bar',
          label: 'Banner clicks',
          data: dailyBannerClicks || [],
          backgroundColor: 'rgba(255, 205, 86, 0.55)',
          borderColor: 'rgba(200, 160, 0, 1)',
          borderWidth: 1,
          yAxisID: 'y',
        },
        {
          type: 'bar',
          label: 'Daily Comments',
          data: dailyComments || [],
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
          yAxisID: 'y',
        },
        {
          type: 'line',
          label: 'Unique Users',
          data: uniqueUsers,
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          tension: 0.1,
          yAxisID: 'y1',
        },
      ],
    };
  }, [chartData]);

  const dailyActivityTableRows = useMemo(() => {
    const d = chartData?.dailyActivity;
    if (!d?.labels?.length) return [];
    const {
      labels, dailyVotes, dailyBannerClicks, dailyComments, uniqueUsers,
    } = d;
    return labels
      .map((date, i) => ({
        key: date,
        date,
        votes: dailyVotes[i],
        bannerClicks: (dailyBannerClicks && dailyBannerClicks[i]) ?? 0,
        comments: dailyComments[i],
        uniqueUsers: uniqueUsers[i],
      }))
      .reverse();
  }, [chartData]);

  // Create chart data for Total User Activity doughnut
  const totalActivityData = useMemo(() => {
    if (!chartData?.totalActivity) return null;

    return {
      labels: chartData.totalActivity.labels,
      datasets: [
        {
          data: chartData.totalActivity.data,
          backgroundColor: [
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)',
            'rgba(255, 205, 86, 0.5)',
            'rgba(75, 192, 192, 0.5)',
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(255, 205, 86, 1)',
            'rgba(75, 192, 192, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  }, [chartData]);

  // Create chart data for Project Overview doughnut
  const projectOverviewData = useMemo(() => {
    if (!chartData?.projectOverview) return null;

    // Generate colors dynamically based on the number of segments
    const colorPalette = [
      'rgba(153, 102, 255, 0.5)', // Purple - Events
      'rgba(255, 159, 64, 0.5)', // Orange - Sessions
      'rgba(54, 162, 235, 0.5)', // Blue - Total Modules
      'rgba(255, 99, 132, 0.5)', // Red - Module type 1
      'rgba(75, 192, 192, 0.5)', // Teal - Module type 2
      'rgba(255, 205, 86, 0.5)', // Yellow - Module type 3
      'rgba(199, 199, 199, 0.5)', // Gray - Module type 4
      'rgba(83, 102, 255, 0.5)', // Blue variant - Module type 5
    ];

    const borderColorPalette = [
      'rgba(153, 102, 255, 1)',
      'rgba(255, 159, 64, 1)',
      'rgba(54, 162, 235, 1)',
      'rgba(255, 99, 132, 1)',
      'rgba(75, 192, 192, 1)',
      'rgba(255, 205, 86, 1)',
      'rgba(199, 199, 199, 1)',
      'rgba(83, 102, 255, 1)',
    ];

    const numSegments = chartData.projectOverview.data.length;
    const backgroundColor = colorPalette.slice(0, numSegments);
    const borderColor = borderColorPalette.slice(0, numSegments);

    return {
      labels: chartData.projectOverview.labels,
      datasets: [
        {
          data: chartData.projectOverview.data,
          backgroundColor,
          borderColor,
          borderWidth: 1,
        },
      ],
    };
  }, [chartData]);

  // Chart options for horizontal bar charts
  const horizontalBarOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      x: {
        beginAtZero: true,
      },
    },
  };

  // Chart options for 1-5 scale horizontal bar charts
  const horizontalBarScaleOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label(context) {
            return `${context.dataset.label}: ${context.parsed.x.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
          callback(value) {
            return value.toString();
          },
        },
        title: {
          display: true,
          text: 'Score (1=Worst, 5=Best)',
        },
      },
    },
  };

  // Chart options for mixed bar/line chart (Daily Activity)
  const mixedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Votes / banner / comments',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Unique Users',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  // Chart options for doughnut charts
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  // Chart options for line charts
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Vote Count',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Date',
        },
      },
    },
  };

  return (
    <div className="charts-page" style={{ padding: '24px' }}>
      <Row align="middle" className="charts-header" style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <h1 style={{ margin: 0 }}>Analytics Dashboard</h1>
        </Col>
        <Col span={16}>
          <Row gutter={16} justify="end" align="middle">
            <Col>
              <Form.Item label="Date Range" style={{ margin: 0 }}>
                <RangePicker
                  format="YYYY-MM-DD"
                  value={getDisplayDateRange()}
                  onChange={handleDateRangeChange}
                  style={{ width: '250px' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
      {loading && (
        <Row justify="center" style={{ marginBottom: '24px' }}>
          <Spin size="large" />
        </Row>
      )}

      {error && (
        <Row style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Alert
              message="Error Loading Analytics Data"
              description={error}
              type="error"
              showIcon
            />
          </Col>
        </Row>
      )}

      {/* Priority Charts Section */}
      <div className="priority-charts">
        <h2 style={{ color: '#1890ff', marginBottom: '24px', fontSize: '20px' }}>
          🎬 TOP Films & Screenings
        </h2>

        {/* Priority Charts Row 1 - TOP Films */}
        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col span={12}>
          <Card title="TOP 10 Films - Total Vote Count" style={{ marginBottom: '24px' }}>
            <div style={{ height: '400px' }}>
              {topFilmsVoteCountData ? (
                <Bar data={topFilmsVoteCountData} options={horizontalBarOptions} />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  {loading ? <Spin /> : 'No data available'}
                </div>
              )}
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="TOP 10 Films - Average Score (1-5)" style={{ marginBottom: '24px' }}>
            <div style={{ height: '400px' }}>
              {topFilmsScoreData ? (
                <Bar data={topFilmsScoreData} options={horizontalBarScaleOptions} />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  {loading ? <Spin /> : 'No data available'}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Priority Charts Row 2 - TOP Screenings */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col span={12}>
          <Card title="TOP 10 Screenings - Total Vote Count" style={{ marginBottom: '24px' }}>
            <div style={{ height: '400px' }}>
              {topScreeningsVoteCountData ? (
                <Bar data={topScreeningsVoteCountData} options={horizontalBarOptions} />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  {loading ? <Spin /> : 'No data available'}
                </div>
              )}
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="TOP 10 Screenings - Average Score (1-5)" style={{ marginBottom: '24px' }}>
            <div style={{ height: '400px' }}>
              {topScreeningsScoreData ? (
                <Bar data={topScreeningsScoreData} options={horizontalBarScaleOptions} />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  {loading ? <Spin /> : 'No data available'}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      </div>

      {/* Additional Analytics Section */}
      <div style={{ marginTop: '48px' }}>
        <h2 style={{ color: '#1890ff', marginBottom: '24px', fontSize: '20px' }}>
          📊 User Activity & Project Analytics
        </h2>

        {/* Additional Charts Row 1 - Daily Activity and Total Activity */}
        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col span={16}>
          <Card title="Daily User Activity" style={{ marginBottom: '24px' }}>
            <div style={{ height: '400px' }}>
              {dailyActivityData ? (
                <Bar data={dailyActivityData} options={mixedChartOptions} />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  {loading ? <Spin /> : 'No data available'}
                </div>
              )}
            </div>
            {dailyActivityTableRows.length > 0 && (
              <Table
                size="small"
                pagination={false}
                style={{ marginTop: 16 }}
                dataSource={dailyActivityTableRows}
                columns={[
                  { title: 'Date', dataIndex: 'date', key: 'date' },
                  {
                    title: 'Votes',
                    dataIndex: 'votes',
                    key: 'votes',
                    align: 'right',
                  },
                  {
                    title: 'Banner clicks',
                    dataIndex: 'bannerClicks',
                    key: 'bannerClicks',
                    align: 'right',
                  },
                  {
                    title: 'Comments',
                    dataIndex: 'comments',
                    key: 'comments',
                    align: 'right',
                  },
                  {
                    title: 'Unique users',
                    dataIndex: 'uniqueUsers',
                    key: 'uniqueUsers',
                    align: 'right',
                  },
                ]}
              />
            )}
          </Card>
        </Col>
        <Col span={8}>
          <Card title="Total User Activity" style={{ marginBottom: '24px' }}>
            <div style={{ height: '400px' }}>
              {totalActivityData ? (
                <Doughnut data={totalActivityData} options={doughnutOptions} />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  {loading ? <Spin /> : 'No data available'}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Additional Charts Row 4 - Project Overview and Popularity Timeline */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card
            title={`Project Overview${chartData?.projectOverview?.totalModules ? ` (${chartData.projectOverview.totalModules} Total Modules)` : ''}`}
            style={{ marginBottom: '24px' }}
          >
            <div style={{ height: '400px' }}>
              {projectOverviewData ? (
                <Doughnut data={projectOverviewData} options={doughnutOptions} />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  {loading ? <Spin /> : 'No data available'}
                </div>
              )}
            </div>
          </Card>
        </Col>
        <Col span={16}>
          <Card title="Popularity per Movie/Module Timeline" style={{ marginBottom: '24px' }}>
            <div style={{ height: '400px' }}>
              {chartData?.popularityTimeline ? (
                <Line data={chartData.popularityTimeline} options={lineOptions} />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  {loading ? <Spin /> : 'No data available'}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
      </div>

      {/* Debug JSON Display - Only for superadmins */}
      {analyticsData && user?.role === 'superadmin' && (
        <div className="debug-panel">
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Card title="Debug: Vote Data">
                <div style={{ marginBottom: '16px' }}>
                  <strong>Votes Summary:</strong> {
                    (() => {
                      if (Array.isArray(analyticsData?.data)) return analyticsData.data.length;
                      if (Array.isArray(analyticsData)) return analyticsData.length;
                      return 0;
                    })()
                  } records found
                </div>
                <pre style={{
                  background: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '4px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  fontSize: '12px',
                  border: '1px solid #d9d9d9',
                }}>
                  {JSON.stringify(analyticsData?.data || analyticsData, null, 2)}
                </pre>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Debug: Events, Sessions & Modules">
                <div style={{ marginBottom: '16px' }}>
                  <strong>Events:</strong> {Array.isArray(analyticsData?.events) ? analyticsData.events.length : 0} found<br/>
                  <strong>Sessions:</strong> {Array.isArray(analyticsData?.sessions) ? analyticsData.sessions.length : 0} found<br/>
                  <strong>Modules:</strong> {Array.isArray(analyticsData?.modules) ? analyticsData.modules.length : 0} found
                </div>
                <pre style={{
                  background: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '4px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  fontSize: '12px',
                  border: '1px solid #d9d9d9',
                }}>
                  {JSON.stringify({
                    events: analyticsData?.events || [],
                    sessions: analyticsData?.sessions || [],
                    modules: analyticsData?.modules || [],
                  }, null, 2)}
                </pre>
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
}

export default ChartsPage;
